import React, { useContext, useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import { Link } from "react-router-dom";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";

import AuthAPI from "../../../api/firebase/AuthAPI";
import UserProfileContext from "../../../context/userProfile/UserProfileContext";
import brandLogo from "../../../assets/images/inproveda-logo.png";

const authAPI = new AuthAPI();

// Google Icon SVG
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" style={{ display: "block" }}>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
    />
  </svg>
);

export default function LoginForm({
  redirect = true,
  handleClose = null,
  initialMode = "signin",
  inDialog = false,
  showHeader = true,
}) {
  const [mode, setMode] = useState(initialMode); // "signin" | "signup" | "forgot"
  const [error, setError] = useState("");
  const [mailSent, setMailSent] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const userProfileContext = useContext(UserProfileContext);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const nameRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const forgotEmailRef = useRef(null);

  useEffect(() => {
    if (userProfileContext.state !== null) {
      setLoading(false);
      setGoogleLoading(false);
    }
  }, [userProfileContext.state]);

  const switchMode = (newMode) => {
    setError("");
    setMailSent("");
    setMode(newMode);
  };

  const handleSignIn = (event) => {
    event.preventDefault();
    setError("");
    setMailSent("");

    const data = new FormData(event.currentTarget);
    const email = (data.get("email") || "").trim();
    const password = data.get("password");

    if (!email) {
      setError("Please enter your email address.");
      emailRef.current?.focus();
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      passwordRef.current?.focus();
      return;
    }

    setLoading(true);
    authAPI.signInWithEmailPassword(email, password).then((res) => {
      setLoading(false);
      if (!res?.status) {
        let errMsg = res?.error?.message || "Login failed. Please check your credentials.";
        const code = res?.error?.code;
        if (
          code === "auth/wrong-password" ||
          code === "auth/user-not-found" ||
          code === "auth/invalid-login-credentials" ||
          code === "auth/invalid-credential"
        ) {
          errMsg = "Invalid email or password. Please verify and try again.";
        } else if (code === "auth/invalid-email") {
          errMsg = "Please enter a valid email address.";
        } else if (code === "auth/too-many-requests") {
          errMsg = "Too many failed attempts. Please reset your password or try again later.";
        }
        setError(errMsg);
      }
    });
  };

  const handleSignUp = (event) => {
    event.preventDefault();
    setError("");
    setMailSent("");

    const data = new FormData(event.currentTarget);
    const name = (data.get("name") || "").trim();
    const email = (data.get("email") || "").trim();
    const password = data.get("password");
    const confirmPassword = data.get("confirm-password");

    if (!name) {
      setError("Please enter your full name.");
      nameRef.current?.focus();
      return;
    }
    if (!email) {
      setError("Please enter a valid email address.");
      emailRef.current?.focus();
      return;
    }
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      passwordRef.current?.focus();
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      confirmPasswordRef.current?.focus();
      return;
    }

    setLoading(true);
    authAPI.signUpWithEmailPassword(name, email, password).then((res) => {
      setLoading(false);
      if (!res?.status) {
        let errMsg = res?.error?.message || "Sign up failed. Please try again.";
        const code = res?.error?.code;
        if (code === "auth/email-already-in-use") {
          errMsg = "An account with this email already exists. Try signing in.";
          emailRef.current?.focus();
        } else if (code === "auth/invalid-email") {
          errMsg = "Please enter a valid email address.";
          emailRef.current?.focus();
        } else if (code === "auth/weak-password") {
          errMsg = "Password should be at least 6 characters.";
          passwordRef.current?.focus();
        }
        setError(errMsg);
      }
    });
  };

  const handleGoogleSignIn = () => {
    setError("");
    setMailSent("");
    setGoogleLoading(true);
    authAPI
      .signInWithGoogle()
      .then(() => {
        setGoogleLoading(false);
      })
      .catch((ex) => {
        setGoogleLoading(false);
        if (ex?.code !== "auth/popup-closed-by-user") {
          setError("Google sign-in could not be completed. Please try again.");
        }
      });
  };

  const handleForgotPassword = (event) => {
    event.preventDefault();
    setError("");
    setMailSent("");

    const emailVal = forgotEmailRef.current?.value?.trim();
    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

    if (!emailVal || !emailVal.match(validRegex)) {
      setError("Please enter a valid email address.");
      forgotEmailRef.current?.focus();
      return;
    }

    setLoading(true);
    authAPI
      .sendResetPasswordLinkOnEmail(emailVal)
      .then(() => {
        setLoading(false);
        setMailSent("Password reset link has been sent to your email.");
      })
      .catch((err) => {
        setLoading(false);
        setError(err?.message || "Could not send reset link. Please check your email.");
      });
  };

  const content = (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        p: { xs: 2.5, sm: 3.5 },
        boxSizing: "border-box",
      }}
    >
      {/* Close button for Dialog mode */}
      {handleClose && (
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="close"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            color: "#64748b",
            bgcolor: "rgba(0, 0, 0, 0.04)",
            zIndex: 10,
            transition: "all 0.2s ease",
            "&:hover": {
              color: "#0f172a",
              bgcolor: "rgba(0, 0, 0, 0.08)",
              transform: "rotate(90deg)",
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      )}

      {/* Brand Header */}
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            mb: 2.5,
            pt: handleClose ? 0.5 : 0,
          }}
        >
          <Box
            component="img"
            src={brandLogo}
            alt="Inproveda"
            sx={{
              height: { xs: 34, sm: 38 },
              maxWidth: "100%",
              objectFit: "contain",
              mb: 1.25,
              filter: "drop-shadow(0 2px 6px rgba(7, 94, 84, 0.08))",
            }}
          />

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.2,
              py: 0.35,
              borderRadius: "999px",
              bgcolor: "rgba(7, 94, 84, 0.07)",
              color: "#075e54",
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.3px",
              mb: 1,
            }}
          >
            <SpaOutlinedIcon sx={{ fontSize: 13 }} />
            <span>Ayurvedic Wisdom • Everyday Wellness</span>
          </Box>

          <Typography
            component="h1"
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#132a24",
              fontSize: { xs: "1.2rem", sm: "1.3rem" },
              lineHeight: 1.25,
            }}
          >
            {mode === "signin" && "Welcome Back"}
            {mode === "signup" && "Create Your Account"}
            {mode === "forgot" && "Reset Password"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#5b6b68",
              fontSize: "0.82rem",
              mt: 0.5,
              maxWidth: 320,
            }}
          >
            {mode === "signin" && "Sign in to access your orders, wishlist & herbal remedies."}
            {mode === "signup" && "Join Inproveda for swift checkout & personalized ayurvedic care."}
            {mode === "forgot" && "Enter your email to receive recovery instructions."}
          </Typography>
        </Box>

      )}

      {/* Segmented Tab Switcher (Sign In vs Register) */}
      {mode !== "forgot" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            bgcolor: "#f0f4f2",
            p: 0.5,
            borderRadius: "14px",
            mb: 2.2,
          }}
        >
          <Button
            type="button"
            onClick={() => switchMode("signin")}
            sx={{
              py: 0.85,
              borderRadius: "11px",
              fontWeight: 700,
              fontSize: "0.85rem",
              textTransform: "none",
              letterSpacing: "0.2px",
              color: mode === "signin" ? "#ffffff" : "#4a6058",
              bgcolor: mode === "signin" ? "#075e54" : "transparent",
              boxShadow: mode === "signin" ? "0 2px 8px rgba(7, 94, 84, 0.25)" : "none",
              "&:hover": {
                bgcolor: mode === "signin" ? "#064e46" : "rgba(7, 94, 84, 0.08)",
              },
            }}
          >
            Sign In
          </Button>
          <Button
            type="button"
            onClick={() => switchMode("signup")}
            sx={{
              py: 0.85,
              borderRadius: "11px",
              fontWeight: 700,
              fontSize: "0.85rem",
              textTransform: "none",
              letterSpacing: "0.2px",
              color: mode === "signup" ? "#ffffff" : "#4a6058",
              bgcolor: mode === "signup" ? "#075e54" : "transparent",
              boxShadow: mode === "signup" ? "0 2px 8px rgba(7, 94, 84, 0.25)" : "none",
              "&:hover": {
                bgcolor: mode === "signup" ? "#064e46" : "rgba(7, 94, 84, 0.08)",
              },
            }}
          >
            Create Account
          </Button>
        </Box>
      )}

      {/* Google Sign In Button */}
      {mode !== "forgot" && (
        <>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading}
            startIcon={googleLoading ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
            sx={{
              py: 1.1,
              borderRadius: "12px",
              borderColor: "#e2e8f0",
              bgcolor: "#ffffff",
              color: "#1e293b",
              fontWeight: 600,
              fontSize: "0.88rem",
              textTransform: "none",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "#f8fafc",
                borderColor: "#cbd5e1",
                boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            {googleLoading
              ? "Connecting to Google..."
              : mode === "signup"
              ? "Sign up with Google"
              : "Continue with Google"}
          </Button>

          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              my: 2.2,
              gap: 1.5,
            }}
          >
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontWeight: 600,
                fontSize: "0.74rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {mode === "signup" ? "or register with email" : "or sign in with email"}
            </Typography>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
          </Box>
        </>
      )}

      {/* Alerts */}
      <Collapse in={Boolean(error)}>
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{
            mb: 2,
            borderRadius: "12px",
            fontSize: "0.82rem",
            fontWeight: 500,
            alignItems: "center",
          }}
        >
          {error}
        </Alert>
      </Collapse>

      <Collapse in={Boolean(mailSent)}>
        <Alert
          severity="success"
          onClose={() => setMailSent("")}
          sx={{
            mb: 2,
            borderRadius: "12px",
            fontSize: "0.82rem",
            fontWeight: 500,
            alignItems: "center",
          }}
        >
          {mailSent}
        </Alert>
      </Collapse>

      {/* SIGN IN FORM */}
      {mode === "signin" && (
        <Box component="form" onSubmit={handleSignIn} noValidate>
          <TextField
            inputRef={emailRef}
            margin="dense"
            required
            fullWidth
            id="signin-email"
            label="Email Address"
            name="email"
            type="email"
            autoComplete="email"
            disabled={loading || googleLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: "#075e54", opacity: 0.75, fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fafcfb",
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          />

          <TextField
            inputRef={passwordRef}
            margin="dense"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            id="signin-password"
            autoComplete="current-password"
            disabled={loading || googleLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#075e54", opacity: 0.75, fontSize: 19 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: "#64748b" }}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 19 }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 19 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fafcfb",
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              type="button"
              variant="text"
              onClick={() => switchMode("forgot")}
              sx={{
                p: 0,
                minWidth: 0,
                color: "#075e54",
                fontWeight: 600,
                fontSize: "0.8rem",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Forgot password?
            </Button>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || googleLoading}
            sx={{
              py: 1.3,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #075e54 0%, #0d7d70 100%)",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "0.92rem",
              textTransform: "none",
              letterSpacing: "0.3px",
              boxShadow: "0 6px 18px -4px rgba(7, 94, 84, 0.4)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #054a42 0%, #075e54 100%)",
                boxShadow: "0 8px 22px -3px rgba(7, 94, 84, 0.5)",
              },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#ffffff" }} /> : "Sign In"}
          </Button>

          <Box sx={{ textAlign: "center", mt: 2.2 }}>
            <Typography variant="body2" sx={{ color: "#5b6b68", fontSize: "0.84rem" }}>
              Don't have an account?{" "}
              <Button
                type="button"
                variant="text"
                onClick={() => switchMode("signup")}
                sx={{
                  p: 0,
                  minWidth: 0,
                  color: "#075e54",
                  fontWeight: 700,
                  fontSize: "inherit",
                  textTransform: "none",
                  verticalAlign: "baseline",
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Sign Up
              </Button>
            </Typography>
          </Box>
        </Box>
      )}

      {/* SIGN UP FORM */}
      {mode === "signup" && (
        <Box component="form" onSubmit={handleSignUp} noValidate>
          <TextField
            inputRef={nameRef}
            margin="dense"
            required
            fullWidth
            id="signup-name"
            label="Full Name"
            name="name"
            autoComplete="name"
            disabled={loading || googleLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineOutlinedIcon sx={{ color: "#075e54", opacity: 0.75, fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fafcfb",
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          />

          <TextField
            inputRef={emailRef}
            margin="dense"
            required
            fullWidth
            id="signup-email"
            label="Email Address"
            name="email"
            type="email"
            autoComplete="email"
            disabled={loading || googleLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: "#075e54", opacity: 0.75, fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fafcfb",
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          />

          <TextField
            inputRef={passwordRef}
            margin="dense"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            id="signup-password"
            autoComplete="new-password"
            disabled={loading || googleLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#075e54", opacity: 0.75, fontSize: 19 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: "#64748b" }}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 19 }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 19 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fafcfb",
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          />

          <TextField
            inputRef={confirmPasswordRef}
            margin="dense"
            required
            fullWidth
            name="confirm-password"
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            id="signup-confirm-password"
            autoComplete="new-password"
            disabled={loading || googleLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#075e54", opacity: 0.75, fontSize: 19 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: "#64748b" }}
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 19 }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 19 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fafcfb",
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          />

          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#64748b",
              lineHeight: 1.45,
              fontSize: "0.72rem",
              mb: 2,
            }}
          >
            By continuing, you agree to Inproveda's{" "}
            <Link to="/terms-conditions" style={{ color: "#075e54", fontWeight: 600 }}>
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" style={{ color: "#075e54", fontWeight: 600 }}>
              Privacy Policy
            </Link>
            .
          </Typography>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || googleLoading}
            sx={{
              py: 1.3,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #075e54 0%, #0d7d70 100%)",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "0.92rem",
              textTransform: "none",
              letterSpacing: "0.3px",
              boxShadow: "0 6px 18px -4px rgba(7, 94, 84, 0.4)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #054a42 0%, #075e54 100%)",
                boxShadow: "0 8px 22px -3px rgba(7, 94, 84, 0.5)",
              },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#ffffff" }} /> : "Create Account"}
          </Button>

          <Box sx={{ textAlign: "center", mt: 2.2 }}>
            <Typography variant="body2" sx={{ color: "#5b6b68", fontSize: "0.84rem" }}>
              Already have an account?{" "}
              <Button
                type="button"
                variant="text"
                onClick={() => switchMode("signin")}
                sx={{
                  p: 0,
                  minWidth: 0,
                  color: "#075e54",
                  fontWeight: 700,
                  fontSize: "inherit",
                  textTransform: "none",
                  verticalAlign: "baseline",
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Sign In
              </Button>
            </Typography>
          </Box>
        </Box>
      )}

      {/* FORGOT PASSWORD FORM */}
      {mode === "forgot" && (
        <Box component="form" onSubmit={handleForgotPassword} noValidate>
          <Box sx={{ mb: 1.5 }}>
            <Button
              type="button"
              variant="text"
              onClick={() => switchMode("signin")}
              startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                p: 0,
                color: "#075e54",
                fontWeight: 600,
                fontSize: "0.82rem",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Back to Sign In
            </Button>
          </Box>

          <TextField
            inputRef={forgotEmailRef}
            margin="dense"
            required
            fullWidth
            id="forgot-email"
            label="Registered Email Address"
            name="email"
            type="email"
            autoComplete="email"
            disabled={loading}
            placeholder="name@example.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: "#075e54", opacity: 0.75, fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fafcfb",
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.3,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #075e54 0%, #0d7d70 100%)",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "0.92rem",
              textTransform: "none",
              letterSpacing: "0.3px",
              boxShadow: "0 6px 18px -4px rgba(7, 94, 84, 0.4)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #054a42 0%, #075e54 100%)",
                boxShadow: "0 8px 22px -3px rgba(7, 94, 84, 0.5)",
              },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#ffffff" }} /> : "Send Reset Link"}
          </Button>

          <Box sx={{ textAlign: "center", mt: 2.2 }}>
            <Typography variant="body2" sx={{ color: "#5b6b68", fontSize: "0.84rem" }}>
              Remembered your password?{" "}
              <Button
                type="button"
                variant="text"
                onClick={() => switchMode("signin")}
                sx={{
                  p: 0,
                  minWidth: 0,
                  color: "#075e54",
                  fontWeight: 700,
                  fontSize: "inherit",
                  textTransform: "none",
                  verticalAlign: "baseline",
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Sign In
              </Button>
            </Typography>
          </Box>
        </Box>
      )}

      {/* Security & Authenticity Trust Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.7,
          mt: 2.5,
          pt: 1.8,
          borderTop: "1px dashed rgba(7, 94, 84, 0.15)",
          color: "#64748b",
          fontSize: "0.74rem",
        }}
      >
        <ShieldOutlinedIcon sx={{ fontSize: 15, color: "#075e54" }} />
        <span>256-Bit SSL Encrypted • 100% Privacy Protected</span>
      </Box>
    </Box>
  );

  if (inDialog) {
    return content;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: 440,
        borderRadius: { xs: "20px", sm: "24px" },
        boxShadow: "0 20px 45px -15px rgba(7, 94, 84, 0.22), 0 0 0 1px rgba(7, 94, 84, 0.08)",
        overflow: "hidden",
        bgcolor: "#ffffff",
        background: "radial-gradient(ellipse at 50% -20%, rgba(7, 94, 84, 0.08), transparent 70%), #ffffff",
      }}
    >
      {content}
    </Paper>
  );
}
